import {
	getAwsCredentials,
	getCognitoAuthSession,
} from 'src/authentication/cognito';
import sigV4Client from 'src/utils/sigV4Client';
import katalMetricsPublisher from 'src/utils/metrics';
import * as KatalMetrics from '@amzn/katal-metrics';
import { logger } from 'src/utils/logger';
import {
	SNOW_DATA_SERVICE_ENDPOINT,
	SNOW_DATA_SERVICE_REGION,
	SDS_APIGATEWAY_ENDPOINT,
} from 'src/config';

const sdsMetricsPublisher =
	katalMetricsPublisher.newChildActionPublisherForMethod('SNOWDataService');
// eslint-disable-next-line @typescript-eslint/no-var-requires
const AWS = require('aws-sdk');

function isAuthError(status) {
	return status === 401 || status === 403;
}

async function forceCredentialRefresh() {
	if (AWS.config.credentials) {
		AWS.config.credentials.expired = true;
	}

	await getAwsCredentials();
}

async function retryWithFreshCredentials(requestParams) {
	await forceCredentialRefresh();

	return callSNOWDataService({
		...requestParams,
		_isRetry: true,
	});
}

export async function callSNOWDataService({
	path,
	method = 'GET',
	headers = {},
	queryParams = {},
	body,
	url = SNOW_DATA_SERVICE_ENDPOINT,
	credentials = null,
	signal = null,
	region = SNOW_DATA_SERVICE_REGION,
	_isRetry = false,
}) {
	const apiCallMetric = new KatalMetrics.Metric.TimedAttempt(
		path
	).withMonitor();
	const requestParams = {
		path,
		method,
		headers,
		queryParams,
		body,
		url,
		credentials,
		signal,
		region,
	};

	try {
		let accessKey;
		let secretKey;
		let sessionToken;

		if (credentials) {
			accessKey = credentials.accessKey;
			secretKey = credentials.secretKey;
			sessionToken = credentials.sessionToken;
		} else {
			await getAwsCredentials();
			accessKey = AWS.config.credentials.accessKeyId;
			secretKey = AWS.config.credentials.secretAccessKey;
			sessionToken = AWS.config.credentials.sessionToken;
		}

		if (!accessKey || !secretKey || !sessionToken) {
			throw new Error(
				'AWS credentials not available. Please ensure you are authenticated. ' +
					'If this error persists, try clearing your browser cache and logging in again.'
			);
		}

		const userName = (
			await getCognitoAuthSession()
		).accessToken.payload.username.split('_')[1];
		const dataToLog =
			method === 'GET' ? JSON.stringify(queryParams) : JSON.stringify(body);

		logger.info(
			`${userName} - Calling SNOWDataService ${path} API with Request : ${dataToLog}`
		);

		const signedSDSCallRequest = sigV4Client
			.newClient({
				accessKey,
				secretKey,
				sessionToken,
				region,
				endpoint: url,
			})
			.signRequest({
				method,
				path,
				headers,
				queryParams,
				body,
			});

		body = body ? JSON.stringify(body) : body;
		headers = signedSDSCallRequest.headers;
		const sdsCallResponse = await fetch(signedSDSCallRequest.url, {
			method,
			signal,
			headers,
			body,
		});

		if (!sdsCallResponse.ok) {
			if (isAuthError(sdsCallResponse.status) && !_isRetry && !credentials) {
				logger.info(
					`SNOWDataService ${path} returned ${sdsCallResponse.status} — refreshing credentials and retrying`
				);

				return retryWithFreshCredentials(requestParams);
			}

			logger.error(
				`${userName} - SNOWDataService ${path} API Response : ${JSON.stringify(
					sdsCallResponse
				)}`
			);
			apiCallMetric.setFailure();

			const responseText = await sdsCallResponse.text();

			if (
				responseText.includes('Authentication') ||
				responseText.includes('credentials') ||
				responseText.includes('Unauthorized') ||
				responseText.includes('Forbidden')
			) {
				throw new AuthError(responseText);
			}

			throw new Error(responseText);
		}

		const isApprovalsCall = path === '/approvals' && url === SDS_APIGATEWAY_ENDPOINT;
		const skipBody =
			!isApprovalsCall &&
			(sdsCallResponse.status === 204 || sdsCallResponse.status === 201);
		let apiResponse;

		if (skipBody) {
			apiResponse = {};
		} else {
			const responseText = await sdsCallResponse.text();

			apiResponse = responseText.trim() === '' ? {} : JSON.parse(responseText);
		}

		logger.info(
			`${userName} - SNOWDataService ${path} API Response : ${JSON.stringify(
				apiResponse
			)}`
		);
		apiCallMetric.setSuccess();

		return apiResponse;
	} catch (e) {
		if (!_isRetry && !credentials && isCredentialError(e)) {
			logger.info(
				`SNOWDataService ${path} credential error — refreshing and retrying`
			);
			try {
				return await retryWithFreshCredentials(requestParams);
			} catch (retryError) {
				apiCallMetric.setFailure();
				logger.error(
					`SNOWDataService ${path} retry also failed - ${retryError.message}`
				);
				throw retryError;
			}
		}

		apiCallMetric.setFailure();
		logger.error(`Exception while calling SNOWDataService API - ${e.message}`);
		throw e;
	} finally {
		sdsMetricsPublisher.publish(apiCallMetric);
	}
}

export class AuthError extends Error {
	constructor(message) {
		super(message);
		this.name = 'AuthError';
	}
}

function isCredentialError(error) {
	if (!error || !error.message) return false;
	const msg = error.message.toLowerCase();

	return (
		msg.includes('authentication failed') ||
		msg.includes('credentials') ||
		msg.includes('token has expired') ||
		msg.includes('not authorized') ||
		msg.includes('security token')
	);
}
